import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ImagePlus, Star,  Banknote, Boxes, CalendarClock, Package, Pencil, Plus, Trash2, Users, X, Archive, ArchiveRestore, ArrowDown, ArrowUp, ArrowUpDown } from "lucide-react";
import { toast } from "sonner";
import api, { fmtDate, formatApiError, money } from "@/lib/api";
import { DeliveriesTab } from "@/components/admin/DeliveriesTab";
import { QuotesTab } from "@/components/admin/QuotesTab";
import { ActivityTab } from "@/components/admin/ActivityTab";
import { TeamTab } from "@/components/admin/TeamTab";
import { PromosTab } from "@/components/admin/PromosTab";
import { RentalStatusCard } from "@/components/admin/RentalStatusCard";
import { BulkBar, bulkBtnCls } from "@/components/admin/BulkBar";
import { useAuth } from "@/context/AuthContext";
import { CATEGORIES } from "@/lib/data";

const emptyForm = {
  title: "", brand: "Oregon Tote Rentals", category: CATEGORIES[0], location: "Bend, OR",
  billing: "daily", daily_rate: "", weekly_rate: "", extra_day_rate: "", flat_rate: "",
  week1: "", week2: "", week3: "", week4: "",
  deposit: "", image: "", description: "", specs: "",
  featured: false, available: true,
};

const inputCls =
  "h-11 w-full rounded-xl border border-slate-200 bg-paper px-4 text-sm font-medium text-ink outline-none transition-colors duration-200 focus:border-royal";

const ADMIN_TABS = [
  ["listings", "Inventory", "inventory"],
  ["deliveries", "Deliveries", "bookings"],
  ["bookings", "Bookings", "bookings"],
  ["quotes", "Quotes", "offers"],
  ["customers", "Customers", "customers"],
  ["promos", "Promos", "offers"],
  ["activity", "Activity", "customers"],
  ["reviews", "Reviews", "customers"],
  ["team", "Team", "owner"],
];

const parseSpecs = (text) => {
  const out = {};
  text.split("\n").forEach((line) => {
    const idx = line.indexOf(":");
    if (idx > 0) out[line.slice(0, idx).trim()] = line.slice(idx + 1).trim();
  });
  return out;
};

const specsToText = (specs) =>
  Object.entries(specs || {}).map(([k, v]) => `${k}: ${v}`).join("\n");

const SortTh = ({ label, k, sort, onSort, right = false, tidPrefix }) => (
  <th className={`px-5 py-4 ${right ? "text-right" : ""}`}>
    <button
      type="button"
      onClick={() => onSort(k)}
      data-testid={`${tidPrefix}-sort-${k}`}
      className={`inline-flex items-center gap-1 font-mono text-[10px] font-semibold uppercase tracking-[0.18em] transition-colors duration-200 hover:text-royal ${sort.key === k ? "text-royal" : ""} ${right ? "ml-auto" : ""}`}
    >
      {label}
      {sort.key === k
        ? (sort.dir === 1 ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />)
        : <ArrowUpDown className="h-3 w-3 opacity-40" />}
    </button>
  </th>
);

export default function AdminPage() {
  const { user } = useAuth();
  const [tab, setTab] = useState("listings");
  const [stats, setStats] = useState(null);
  const [listings, setListings] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [bookingFilter, setBookingFilter] = useState("all");
  const [bookSort, setBookSort] = useState({ key: "created", dir: -1 });
  const [custSort, setCustSort] = useState({ key: "last_booking", dir: -1 });
  const [showArchivedCustomers, setShowArchivedCustomers] = useState(false);
  const [bookSel, setBookSel] = useState(new Set());
  const [custSel, setCustSel] = useState(new Set());
  const [listSel, setListSel] = useState(new Set());
  const [reviewSel, setReviewSel] = useState(new Set());
  const [bookSearch, setBookSearch] = useState("");
  const [custSearch, setCustSearch] = useState("");
  const [listingCat, setListingCat] = useState("");
  const [listingStatus, setListingStatus] = useState("all");
  const [reviewRating, setReviewRating] = useState("all");
  const [autoArchive, setAutoArchive] = useState(null);
  const [aaOpen, setAaOpen] = useState(false);
  const [aaDays, setAaDays] = useState(7);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [busy, setBusy] = useState(false);
  const [orderOpen, setOrderOpen] = useState(false);
  const [orderForm, setOrderForm] = useState({
    listing_id: "", start_date: "", end_date: "", name: "", email: "", phone: "", zip: "",
    card_holder: "", license_number: "", address: "", purpose: "residential",
    alt_name: "", alt_phone: "", alt_email: "", notes: "", referred_by: "",
    extra_dollies: 0, self_pickup: false, with_package: false, packing_kit: false, dolly_bundle: false,
    delivery_window: "morning", pickup_window: "morning",
  });
  const [customers, setCustomers] = useState([]);
  const [adminReviews, setAdminReviews] = useState([]);
  const [detail, setDetail] = useState(null);
  const [feeModal, setFeeModal] = useState(null);
  const [feeForm, setFeeForm] = useState({ tote_count: 1, fee_per_tote: 20, note: "" });

  const loadBookings = () =>
    api.get(`/admin/bookings${bookingFilter === "archived" ? "?archived=true" : ""}`).then((r) => setBookings(r.data)).catch(() => {});
  const loadCustomers = () =>
    api.get(`/admin/customers${showArchivedCustomers ? "?archived=true" : ""}`).then((r) => setCustomers(r.data)).catch(() => {});

  const load = () => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/admin/listings").then((r) => setListings(r.data)).catch(() => {});
    loadBookings();
    loadCustomers();
    api.get("/admin/reviews").then((r) => setAdminReviews(r.data)).catch(() => {});
    api.get("/admin/auto-archive").then((r) => { setAutoArchive(r.data); setAaDays(r.data.days); }).catch(() => {});
  };

  const visibleTabs = ADMIN_TABS.filter(([, , perm]) =>
    user?.is_owner || (perm !== "owner" && (user?.permissions || []).includes(perm)));

  useEffect(() => {
    if (user && user.role === "admin") load();
  }, [user]);

  useEffect(() => {
    if (user && user.role === "admin") loadBookings();
  }, [bookingFilter]);

  useEffect(() => {
    if (user && user.role === "admin") loadCustomers();
  }, [showArchivedCustomers]);

  useEffect(() => {
    if (user && user.role === "admin" && visibleTabs.length && !visibleTabs.some(([k]) => k === tab)) {
      setTab(visibleTabs[0][0]);
    }
  }, [user]);

  if (user === null) return <main className="min-h-[60vh]" />;
  if (!user || user.role !== "admin") return <Navigate to="/" replace />;

  const openAdd = () => {
    setForm(emptyForm);
    setModal({ mode: "add" });
  };

  const openEdit = (l) => {
    setForm({
      title: l.title, brand: l.brand, category: l.category, location: l.location,
      billing: l.billing || "daily", daily_rate: l.daily_rate ?? "",
      weekly_rate: l.weekly_rate ?? "", extra_day_rate: l.extra_day_rate ?? "",
      flat_rate: l.flat_rate ?? "",
      week1: l.week_prices?.[0] ?? "", week2: l.week_prices?.[1] ?? "",
      week3: l.week_prices?.[2] ?? "", week4: l.week_prices?.[3] ?? "",
      deposit: l.deposit, image: l.image,
      description: l.description, specs: specsToText(l.specs),
      featured: !!l.featured, available: !!l.available,
    });
    setModal({ mode: "edit", id: l.id });
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const payload = {
      ...form,
      daily_rate: Number(form.daily_rate) || 0,
      weekly_rate: Number(form.weekly_rate) || 0,
      extra_day_rate: Number(form.extra_day_rate) || 0,
      flat_rate: Number(form.flat_rate) || 0,
      week_prices: form.billing === "tiered"
        ? [form.week1, form.week2, form.week3, form.week4].map((v) => Number(v) || 0)
        : [],
      deposit: 0,
      specs: parseSpecs(form.specs),
    };
    delete payload.week1;
    delete payload.week2;
    delete payload.week3;
    delete payload.week4;
    try {
      if (modal.mode === "add") {
        await api.post("/listings", payload);
        toast.success("Package added to the lineup");
      } else {
        await api.put(`/listings/${modal.id}`, payload);
        toast.success("Package updated");
      }
      setModal(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this package?")) return;
    try {
      await api.delete(`/listings/${id}`);
      toast.success("Package removed");
      load();
    } catch {
      toast.error("Could not remove listing");
    }
  };

  const cancelBooking = async (id) => {
    try {
      await api.patch(`/bookings/${id}/cancel`);
      toast.success("Booking cancelled");
      load();
    } catch {
      toast.error("Could not cancel booking");
    }
  };

  const archiveBooking = async (b, archived) => {
    try {
      await api.post(`/admin/bookings/${b.id}/${archived ? "archive" : "restore"}`);
      toast.success(archived ? `Order ${b.reference} archived — find it under the Archived filter` : `Order ${b.reference} restored`);
      loadBookings();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const deleteBooking = async (b) => {
    if (!window.confirm(`Permanently delete order ${b.reference} (${b.customer?.name || b.user_email})? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/bookings/${b.id}`);
      toast.success(`Order ${b.reference} permanently deleted`);
      loadBookings();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const archiveCustomer = async (c, archived) => {
    try {
      await api.post("/admin/customers/archive", { email: c.email, archived });
      toast.success(archived ? `${c.name || c.email} archived — recoverable anytime` : `${c.name || c.email} restored`);
      loadCustomers();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const deleteCustomer = async (c) => {
    if (!window.confirm(`Permanently delete the account for ${c.email}? Their order history stays on record. This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/customers/${c.email}`);
      toast.success(`${c.email} account deleted — order history kept`);
      loadCustomers();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const toggleBookSort = (k) => setBookSort((s) => ({ key: k, dir: s.key === k ? -s.dir : 1 }));
  const toggleCustSort = (k) => setCustSort((s) => ({ key: k, dir: s.key === k ? -s.dir : 1 }));

  const toggleSel = (setter) => (id) =>
    setter((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  const toggleAllIn = (ids, setter) =>
    setter((s) => (ids.length > 0 && ids.every((id) => s.has(id)) ? new Set() : new Set(ids)));

  const bulkSummary = (data, noun) => {
    const skipNote = data.skipped?.length ? ` — skipped ${data.skipped.length} (protected or not eligible)` : "";
    toast.success(`${data.done} ${noun} updated${skipNote}`);
  };

  const bulkBookings = async (action) => {
    const ids = [...bookSel];
    if (action === "delete" && !window.confirm(`Permanently delete ${ids.length} selected order(s)? Only cancelled or archived orders are deleted — the rest are skipped. This cannot be undone.`)) return;
    try {
      const { data } = await api.post("/admin/bookings/bulk", { ids, action });
      bulkSummary(data, "order(s)");
      setBookSel(new Set());
      loadBookings();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const bulkCustomers = async (action) => {
    const emails = [...custSel];
    if (action === "delete" && !window.confirm(`Permanently delete ${emails.length} selected customer account(s)? Accounts with live orders are skipped. This cannot be undone.`)) return;
    try {
      const { data } = await api.post("/admin/customers/bulk", { emails, action });
      bulkSummary(data, "customer(s)");
      setCustSel(new Set());
      loadCustomers();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const bulkListings = async () => {
    const ids = [...listSel];
    if (!window.confirm(`Permanently delete ${ids.length} selected listing(s)? This cannot be undone.`)) return;
    try {
      const { data } = await api.post("/admin/listings/bulk", { ids, action: "delete" });
      bulkSummary(data, "listing(s)");
      setListSel(new Set());
      api.get("/admin/listings").then((r) => setListings(r.data)).catch(() => {});
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const bulkReviews = async () => {
    const ids = [...reviewSel];
    if (!window.confirm(`Permanently delete ${ids.length} selected review(s)? Seeded showcase reviews are protected. This cannot be undone.`)) return;
    try {
      const { data } = await api.post("/admin/reviews/bulk", { ids, action: "delete" });
      bulkSummary(data, "review(s)");
      setReviewSel(new Set());
      api.get("/admin/reviews").then((r) => setAdminReviews(r.data)).catch(() => {});
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const saveAutoArchive = async (enabled) => {
    try {
      const { data } = await api.post("/admin/auto-archive", { enabled, days: Number(aaDays) || 7 });
      setAutoArchive(data);
      if (enabled) {
        toast.success(data.archived_now
          ? `Auto-archive on — ${data.archived_now} done order(s) archived right away`
          : `Auto-archive on — orders archive ${data.days} day(s) after pickup`);
      } else {
        toast.success("Auto-archive off");
      }
      loadBookings();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const runAutoArchive = async () => {
    try {
      const { data } = await api.post("/admin/auto-archive/run");
      toast.success(data.archived ? `Archived ${data.archived} done order(s)` : "Nothing to archive right now");
      loadBookings();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const openOrder = (cust = null) => {
    setOrderForm({
      listing_id: listings[0]?.id || "", start_date: "", end_date: "",
      name: cust?.name || "", email: cust?.email || "", phone: cust?.phone || "", zip: cust?.zip || "",
      card_holder: "", license_number: "", address: "", purpose: "residential",
      alt_name: "", alt_phone: "", alt_email: "", notes: "", referred_by: "",
      extra_dollies: 0, self_pickup: false, with_package: false,
      packing_kit: false, dolly_bundle: false,
      delivery_window: "morning", pickup_window: "morning",
    });
    setOrderOpen(true);
  };

  const deleteReview = async (id) => {
    setBusy(true);
    try {
      await api.delete(`/admin/reviews/${id}`);
      toast.success("Review deleted");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const uploadListingImage = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload-image", fd);
      setForm((f) => ({ ...f, image: `/api/files/${data.path}` }));
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const sendFeeLink = async (b) => {
    setBusy(true);
    try {
      await api.post(`/admin/bookings/${b.id}/damage-fee-link`, { origin_url: window.location.origin });
      toast.success(`Payment link emailed to ${b.customer?.email || b.user_email}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const submitOrder = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/admin/bookings", {
        listing_id: orderForm.listing_id,
        start_date: orderForm.start_date,
        end_date: orderForm.end_date,
        customer: {
          name: orderForm.name, email: orderForm.email, phone: orderForm.phone, zip: orderForm.zip,
          card_holder: orderForm.card_holder || orderForm.name, address: orderForm.address,
          license_number: orderForm.license_number,
          purpose: orderForm.purpose, alt_name: orderForm.alt_name, alt_phone: orderForm.alt_phone,
          alt_email: orderForm.alt_email, notes: orderForm.notes,
        },
        extra_dollies: Number(orderForm.extra_dollies) || 0,
        delivery_window: orderForm.delivery_window,
        pickup_window: orderForm.pickup_window,
        self_pickup: orderForm.self_pickup,
        with_package: orderForm.with_package,
        packing_kit: orderForm.packing_kit,
        dolly_bundle: orderForm.dolly_bundle,
      });
      toast.success("Rental order created");
      setOrderOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const openFee = (booking) => {
    setFeeForm({ tote_count: 1, fee_per_tote: 20, note: "" });
    setFeeModal(booking);
  };

  const submitFee = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/admin/bookings/${feeModal.id}/damage-fee`, {
        tote_count: Number(feeForm.tote_count),
        fee_per_tote: Number(feeForm.fee_per_tote),
        note: feeForm.note,
      });
      toast.success("Damage fee recorded");
      setFeeModal(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const sendReminders = async () => {
    try {
      const { data } = await api.post("/admin/send-reminders");
      toast.success(data.sent ? `${data.sent} balance reminder${data.sent === 1 ? "" : "s"} sent` : "No deliveries tomorrow — nothing to send");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const todayIso = new Date().toISOString().slice(0, 10);
  const bookQuery = bookSearch.trim().toLowerCase();
  const filteredBookings = bookings.filter((b) => {
    if (bookQuery) {
      const hay = [b.reference, b.listing_title, b.customer?.name, b.customer?.email, b.user_email, b.delivery_zip]
        .filter(Boolean).join(" ").toLowerCase();
      if (!hay.includes(bookQuery)) return false;
    }
    if (bookingFilter === "archived") return true; // server already filtered to archived
    if (bookingFilter === "pending") return b.status === "confirmed" && b.start_date >= todayIso;
    if (bookingFilter === "active") return b.status === "confirmed" && b.start_date <= todayIso && b.end_date >= todayIso;
    if (bookingFilter === "past") return b.status === "confirmed" && b.end_date < todayIso;
    if (bookingFilter === "cancelled") return b.status === "cancelled";
    return true;
  });
  const sortedBookings = [...filteredBookings].sort((a, b) => {
    const d = bookSort.dir;
    switch (bookSort.key) {
      case "reference": return d * a.reference.localeCompare(b.reference);
      case "listing": return d * a.listing_title.localeCompare(b.listing_title);
      case "customer": return d * (a.customer?.name || a.user_email || "").localeCompare(b.customer?.name || b.user_email || "");
      case "window": return d * a.start_date.localeCompare(b.start_date);
      case "total": return d * ((a.total || 0) - (b.total || 0));
      case "status": return d * a.status.localeCompare(b.status);
      case "created":
      default: return d * ((a.created_at || "").localeCompare(b.created_at || ""));
    }
  });
  const custQuery = custSearch.trim().toLowerCase();
  const visibleCustomers = custQuery
    ? customers.filter((c) => [c.name, c.email, c.zip].filter(Boolean).join(" ").toLowerCase().includes(custQuery))
    : customers;
  const sortedCustomers = [...visibleCustomers].sort((a, b) => {
    const d = custSort.dir;
    switch (custSort.key) {
      case "name": return d * (a.name || a.email).localeCompare(b.name || b.email);
      case "orders": return d * (a.orders - b.orders);
      case "total_spent": return d * (a.total_spent - b.total_spent);
      case "last_booking":
      default: return d * (a.last_booking || "").localeCompare(b.last_booking || "");
    }
  });
  const visibleListings = listings.filter((l) =>
    (!listingCat || l.category === listingCat) &&
    (listingStatus === "all" || (listingStatus === "live" ? l.available : !l.available)));
  const visibleReviews = adminReviews.filter((r) => reviewRating === "all" || r.rating === Number(reviewRating));

  const statCards = [
    { label: "Rental revenue", value: money(stats?.revenue), icon: Banknote },
    { label: "Active rentals", value: stats?.active_rentals ?? "—", icon: CalendarClock },
    { label: "Inventory", value: stats?.listings ?? "—", icon: Boxes },
    { label: "Members", value: stats?.customers ?? "—", icon: Users },
  ];

  return (
    <main className="bg-paper pb-28" data-testid="admin-page">
      <section className="border-b border-slate-200 bg-ink text-white">
        <div className="mx-auto max-w-7xl px-4 py-14 sm:px-8 lg:px-16">
          <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.25em] text-royal">
            Tote HQ
          </p>
          <h1 className="mt-2 font-display text-3xl font-extrabold tracking-tight sm:text-4xl">
            Command deck.
          </h1>
          <div className="mt-9 grid grid-cols-2 gap-4 lg:grid-cols-4">
            {statCards.map((s) => (
              <div key={s.label} className="rounded-2xl border border-white/10 bg-white/5 p-5 backdrop-blur">
                <s.icon className="h-5 w-5 text-royal" />
                <p className="mt-3 font-display text-2xl font-extrabold">{s.value}</p>
                <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.18em] text-white/50">
                  {s.label}
                </p>
              </div>
            ))}
          </div>
          {stats?.tote_inventory != null && (
            <div className="mt-4 rounded-2xl border border-white/10 bg-white/5 p-5 backdrop-blur" data-testid="tote-pool-card">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <p className="flex items-center gap-2 font-mono text-[10px] font-semibold uppercase tracking-[0.18em] text-white/50">
                  <Package className="h-4 w-4 text-royal" /> Tote pool — out right now
                </p>
                <p className="font-display text-sm font-bold" data-testid="tote-pool-count">
                  <span className={stats.totes_out > 0 ? "text-gold" : "text-emerald-400"}>{stats.totes_out} out</span>
                  <span className="mx-2 text-white/30">·</span>
                  <span className="text-emerald-400">{stats.tote_inventory - stats.totes_out} available</span>
                  <span className="ml-2 font-mono text-[10px] font-medium uppercase tracking-[0.15em] text-white/40">of {stats.tote_inventory} totes</span>
                </p>
              </div>
              <div className="mt-3 h-2.5 w-full overflow-hidden rounded-full bg-white/10" data-testid="tote-pool-bar">
                <div
                  className={`h-full rounded-full transition-all duration-500 ${stats.totes_out / stats.tote_inventory >= 0.75 ? "bg-gold" : "bg-royal"}`}
                  style={{ width: `${Math.min(100, Math.round((stats.totes_out / stats.tote_inventory) * 100))}%` }}
                />
              </div>
            </div>
          )}
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 pt-10 sm:px-8 lg:px-16">
        {user?.is_owner && (
          <div className="mb-6">
            <RentalStatusCard />
          </div>
        )}
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex flex-wrap gap-2 rounded-2xl border border-slate-200 bg-white p-1.5">
            {visibleTabs.map(([key, label]) => (
              <button
                key={key}
                onClick={() => setTab(key)}
                data-testid={`admin-tab-${key}`}
                className={`rounded-xl px-5 py-2.5 text-sm font-semibold transition-all duration-200 ${
                  tab === key ? "bg-ink text-white shadow-md" : "text-slate-500 hover:text-ink"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
          {tab === "listings" && (
            <button
              onClick={openAdd}
              data-testid="admin-add-listing-button"
              className="flex items-center gap-2 rounded-full bg-royal px-5 py-2.5 text-sm font-semibold text-white transition-all duration-200 hover:scale-[1.02] hover:bg-royal-dark hover:shadow-lg hover:shadow-royal/25 active:scale-[0.98]"
            >
              <Plus className="h-4 w-4" /> Add package
            </button>
          )}
          {tab === "bookings" && (
            <div className="flex flex-wrap gap-2">
              <button
                onClick={sendReminders}
                data-testid="admin-send-reminders-button"
                className="flex items-center gap-2 rounded-full border border-slate-200 bg-white px-5 py-2.5 text-sm font-semibold text-slate-600 transition-colors duration-200 hover:border-royal hover:text-royal"
              >
                Email balance reminders
              </button>
              <button
                onClick={openOrder}
                data-testid="admin-add-booking-button"
                className="flex items-center gap-2 rounded-full bg-royal px-5 py-2.5 text-sm font-semibold text-white transition-all duration-200 hover:scale-[1.02] hover:bg-royal-dark hover:shadow-lg hover:shadow-royal/25 active:scale-[0.98]"
              >
                <Plus className="h-4 w-4" /> New order
              </button>
            </div>
          )}
        </div>

        {tab === "listings" && (
          <>
          <div className="mt-8 flex flex-wrap items-center gap-2" data-testid="listing-filters">
            <select
              value={listingCat}
              onChange={(e) => setListingCat(e.target.value)}
              data-testid="listing-filter-category"
              className="h-10 rounded-xl border border-slate-200 bg-white px-3 text-xs font-semibold text-slate-600 outline-none transition-colors duration-200 focus:border-royal"
            >
              <option value="">All categories</option>
              {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            {[["all", "All"], ["live", "Live"], ["offline", "Offline"]].map(([key, label]) => (
              <button
                key={key}
                onClick={() => setListingStatus(key)}
                data-testid={`listing-filter-${key}`}
                className={`rounded-full px-4 py-2 text-xs font-semibold transition-all duration-200 ${
                  listingStatus === key ? "bg-ink text-white shadow-md" : "border border-slate-200 bg-white text-slate-500 hover:border-royal hover:text-royal"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
          <BulkBar count={listSel.size} onClear={() => setListSel(new Set())} testId="bulk-listings-bar">
            <button onClick={bulkListings} data-testid="bulk-listings-delete" className={`${bulkBtnCls} hover:border-red-300 hover:text-red-600`}>
              Delete selected
            </button>
          </BulkBar>
          <div className="mt-4 overflow-x-auto rounded-2xl border border-slate-200 bg-white">
            <table className="w-full min-w-[760px] text-left text-sm">
              <thead>
                <tr className="border-b border-slate-200 font-mono text-[10px] uppercase tracking-[0.18em] text-slate-500">
                  <th className="w-10 px-4 py-4">
                    <input
                      type="checkbox"
                      checked={visibleListings.length > 0 && visibleListings.every((l) => listSel.has(l.id))}
                      onChange={() => toggleAllIn(visibleListings.map((l) => l.id), setListSel)}
                      data-testid="listings-select-all"
                      className="h-4 w-4 accent-[#1D4ED8]"
                    />
                  </th>
                  <th className="px-5 py-4">Package</th>
                  <th className="px-5 py-4">Category</th>
                  <th className="px-5 py-4">Rate</th>
                  <th className="px-5 py-4">Status</th>
                  <th className="px-5 py-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {visibleListings.map((l) => (
                  <tr key={l.id} className="border-b border-slate-100 transition-colors duration-200 hover:bg-royal-light/40">
                    <td className="px-4 py-4">
                      <input
                        type="checkbox"
                        checked={listSel.has(l.id)}
                        onChange={() => toggleSel(setListSel)(l.id)}
                        data-testid={`listing-select-${l.id}`}
                        className="h-4 w-4 accent-[#1D4ED8]"
                      />
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-3">
                        <img src={l.image} alt="" className="h-11 w-14 rounded-lg object-cover" />
                        <div>
                          <p className="font-semibold text-ink">{l.title}</p>
                          <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-slate-400">
                            {l.brand} {l.featured ? "· featured" : ""}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-4 text-slate-600">{l.category}</td>
                    <td className="px-5 py-4 font-semibold text-ink">
                      {l.billing === "weekly" ? `${money(l.weekly_rate)}/wk` : l.billing === "once" ? `${money(l.flat_rate)} once` : l.billing === "dual" ? `${money(l.daily_rate)}/day · ${money(l.weekly_rate)}/wk w/pkg` : l.billing === "tiered" ? `from ${money((l.week_prices || [])[0] || l.weekly_rate)}/wk` : l.billing === "call" ? "Call" : money(l.daily_rate)}
                    </td>
                    <td className="px-5 py-4">
                      <span className={`rounded-full px-2.5 py-1 font-mono text-[10px] font-semibold uppercase tracking-[0.12em] ${l.available ? "bg-emerald-50 text-emerald-600" : "bg-slate-100 text-slate-500"}`}>
                        {l.available ? "Live" : "Offline"}
                      </span>
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => openEdit(l)}
                          data-testid={`listing-edit-button-${l.id}`}
                          aria-label="Edit listing"
                          className="grid h-9 w-9 place-items-center rounded-full border border-slate-200 text-slate-500 transition-colors duration-200 hover:border-royal hover:text-royal"
                        >
                          <Pencil className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => remove(l.id)}
                          data-testid={`listing-delete-button-${l.id}`}
                          aria-label="Delete listing"
                          className="grid h-9 w-9 place-items-center rounded-full border border-slate-200 text-slate-500 transition-colors duration-200 hover:border-red-300 hover:text-red-600"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          </>
        )}

        {tab === "bookings" && (
          <>
            <div className="mt-8 flex flex-wrap items-center gap-2" data-testid="booking-filters">
              {[["all", "All"], ["pending", "Pending delivery"], ["active", "Out now"], ["past", "Past"], ["cancelled", "Cancelled"], ["archived", "Archived"]].map(([key, label]) => (
                <button
                  key={key}
                  onClick={() => setBookingFilter(key)}
                  data-testid={`booking-filter-${key}`}
                  className={`rounded-full px-4 py-2 text-xs font-semibold transition-all duration-200 ${
                    bookingFilter === key ? "bg-ink text-white shadow-md" : "border border-slate-200 bg-white text-slate-500 hover:border-royal hover:text-royal"
                  }`}
                >
                  {label}
                </button>
              ))}
              <input
                value={bookSearch}
                onChange={(e) => setBookSearch(e.target.value)}
                placeholder="Filter by reference, customer, email or zip…"
                data-testid="booking-search-input"
                className="h-10 min-w-[220px] flex-1 rounded-xl border border-slate-200 bg-white px-4 text-xs font-medium text-ink outline-none transition-colors duration-200 focus:border-royal"
              />
              <button
                onClick={() => setAaOpen((v) => !v)}
                data-testid="auto-archive-toggle"
                className={`rounded-full px-4 py-2 text-xs font-semibold transition-all duration-200 ${
                  autoArchive?.enabled ? "bg-emerald-600 text-white shadow-md" : "border border-slate-200 bg-white text-slate-500 hover:border-royal hover:text-royal"
                }`}
              >
                Auto-archive: {autoArchive?.enabled ? `${autoArchive.days}d after pickup` : "off"}
              </button>
              <a
                href={`${api.defaults.baseURL}/admin/bookings/export?view=${bookingFilter}&q=${encodeURIComponent(bookSearch)}`}
                data-testid="export-bookings-csv"
                className="rounded-full border border-slate-200 bg-white px-4 py-2 text-xs font-semibold text-slate-500 transition-all duration-200 hover:border-royal hover:text-royal"
              >
                Export CSV
              </a>
            </div>
            {aaOpen && (
              <div className="mt-3 flex flex-wrap items-center gap-3 rounded-2xl border border-slate-200 bg-white px-5 py-4" data-testid="auto-archive-panel">
                <p className="text-xs font-semibold text-ink">Automatically archive picked-up orders after</p>
                <input
                  type="number" min="0" max="365" value={aaDays}
                  onChange={(e) => setAaDays(e.target.value)}
                  data-testid="auto-archive-days-input"
                  className="h-9 w-20 rounded-xl border border-slate-200 bg-paper px-3 text-sm font-medium text-ink outline-none transition-colors duration-200 focus:border-royal"
                />
                <span className="text-xs text-slate-500">days</span>
                {autoArchive?.enabled ? (
                  <button onClick={() => saveAutoArchive(false)} data-testid="auto-archive-disable"
                          className="rounded-full bg-ink px-4 py-2 text-xs font-semibold text-white transition-colors duration-200 hover:bg-royal">
                    Turn off
                  </button>
                ) : (
                  <button onClick={() => saveAutoArchive(true)} data-testid="auto-archive-enable"
                          className="rounded-full bg-emerald-600 px-4 py-2 text-xs font-semibold text-white transition-colors duration-200 hover:bg-emerald-700">
                    Turn on
                  </button>
                )}
                {autoArchive?.enabled && (
                  <button onClick={() => saveAutoArchive(true)} data-testid="auto-archive-save"
                          className="rounded-full border border-slate-200 bg-white px-4 py-2 text-xs font-semibold text-slate-500 transition-colors duration-200 hover:border-royal hover:text-royal">
                    Save days
                  </button>
                )}
                <button onClick={runAutoArchive} data-testid="auto-archive-run"
                        className="rounded-full border border-slate-200 bg-white px-4 py-2 text-xs font-semibold text-slate-500 transition-colors duration-200 hover:border-royal hover:text-royal">
                  Run now
                </button>
                <p className="w-full text-[11px] text-slate-400">Archived orders stay recoverable under the Archived filter. The sweep also runs automatically every hour.</p>
              </div>
            )}
          <BulkBar count={bookSel.size} onClear={() => setBookSel(new Set())} testId="bulk-bookings-bar">
            {bookingFilter === "archived" ? (
              <button onClick={() => bulkBookings("restore")} data-testid="bulk-bookings-restore" className={bulkBtnCls}>
                Restore selected
              </button>
            ) : (
              <button onClick={() => bulkBookings("archive")} data-testid="bulk-bookings-archive" className={bulkBtnCls}>
                Archive selected
              </button>
            )}
            <button onClick={() => bulkBookings("delete")} data-testid="bulk-bookings-delete" className={`${bulkBtnCls} hover:border-red-300 hover:text-red-600`}>
              Delete selected
            </button>
          </BulkBar>
          <div className="mt-4 overflow-x-auto rounded-2xl border border-slate-200 bg-white">
            <table className="w-full min-w-[760px] text-left text-sm">
              <thead>
                <tr className="border-b border-slate-200 font-mono text-[10px] uppercase tracking-[0.18em] text-slate-500">
                  <th className="w-10 px-4 py-4">
                    <input
                      type="checkbox"
                      checked={sortedBookings.length > 0 && sortedBookings.every((b) => bookSel.has(b.id))}
                      onChange={() => toggleAllIn(sortedBookings.map((b) => b.id), setBookSel)}
                      data-testid="bookings-select-all"
                      className="h-4 w-4 accent-[#1D4ED8]"
                    />
                  </th>
                  <SortTh label="Reference" k="reference" sort={bookSort} onSort={toggleBookSort} tidPrefix="bookings" />
                  <SortTh label="Package" k="listing" sort={bookSort} onSort={toggleBookSort} tidPrefix="bookings" />
                  <SortTh label="Customer" k="customer" sort={bookSort} onSort={toggleBookSort} tidPrefix="bookings" />
                  <SortTh label="Window" k="window" sort={bookSort} onSort={toggleBookSort} tidPrefix="bookings" />
                  <SortTh label="Total" k="total" sort={bookSort} onSort={toggleBookSort} tidPrefix="bookings" />
                  <SortTh label="Status" k="status" sort={bookSort} onSort={toggleBookSort} tidPrefix="bookings" />
                  <th className="px-5 py-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {sortedBookings.length === 0 && (
                  <tr><td colSpan={8} className="px-5 py-12 text-center text-slate-400">No bookings in this view.</td></tr>
                )}
                {sortedBookings.map((b) => (
                  <tr key={b.id} onClick={() => setDetail(b)} data-testid={`booking-row-${b.id}`} className="cursor-pointer border-b border-slate-100 transition-colors duration-200 hover:bg-royal-light/40">
                    <td className="px-4 py-4" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={bookSel.has(b.id)}
                        onChange={() => toggleSel(setBookSel)(b.id)}
                        data-testid={`booking-select-${b.id}`}
                        className="h-4 w-4 accent-[#1D4ED8]"
                      />
                    </td>
                    <td className="px-5 py-4 font-mono text-xs font-semibold text-royal">{b.reference}</td>
                    <td className="px-5 py-4 font-semibold text-ink">{b.listing_title}</td>
                    <td className="px-5 py-4 text-slate-600">
                      {b.user_email}{b.delivery_zip ? ` · ${b.delivery_zip}` : ""}
                      {b.source === "admin" && (
                        <span className="ml-2 rounded-full bg-royal-light px-2 py-0.5 font-mono text-[9px] font-semibold uppercase tracking-[0.12em] text-royal">
                          manual
                        </span>
                      )}
                      {b.customer?.referred_by && (
                        <span className="ml-2 rounded-full bg-amber-50 px-2 py-0.5 font-mono text-[9px] font-semibold uppercase tracking-[0.12em] text-amber-700" data-testid={`referral-badge-${b.id}`}>
                          referral
                        </span>
                      )}
                    </td>
                    <td className="px-5 py-4 text-slate-600">{fmtDate(b.start_date)} – {fmtDate(b.end_date)}</td>
                    <td className="px-5 py-4 font-semibold text-ink">{money(b.total)}</td>
                    <td className="px-5 py-4">
                      <span className={`rounded-full px-2.5 py-1 font-mono text-[10px] font-semibold uppercase tracking-[0.12em] ${b.status === "confirmed" ? "bg-emerald-50 text-emerald-600" : "bg-slate-100 text-slate-500"}`}>
                        {b.status}
                      </span>
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex items-center justify-end gap-2" onClick={(e) => e.stopPropagation()}>
                        {b.damage_fees_total > 0 && (
                          <span className={`rounded-full px-2.5 py-1 font-mono text-[10px] font-semibold uppercase tracking-[0.12em] ${b.damage_fees_status === "paid" ? "bg-emerald-50 text-emerald-600" : "bg-amber-50 text-amber-700"}`} data-testid={`fees-badge-${b.id}`}>
                            {money(b.damage_fees_total)} fees{b.damage_fees_status === "paid" ? " paid" : b.damage_fees_status === "sent" ? " sent" : ""}
                          </span>
                        )}
                        {(b.damage_fees_total || 0) > (b.damage_fees_paid || 0) && (
                          <button
                            onClick={() => sendFeeLink(b)}
                            data-testid={`fee-link-button-${b.id}`}
                            className="rounded-full border border-slate-200 px-4 py-1.5 text-xs font-semibold text-slate-500 transition-colors duration-200 hover:border-royal hover:text-royal"
                          >
                            Email link
                          </button>
                        )}
                        <button
                          onClick={() => openFee(b)}
                          data-testid={`damage-fee-button-${b.id}`}
                          className="rounded-full border border-slate-200 px-4 py-1.5 text-xs font-semibold text-slate-500 transition-colors duration-200 hover:border-amber-400 hover:text-amber-700"
                        >
                          Damage fee
                        </button>
                        {b.status === "confirmed" && (
                          <button
                            onClick={() => cancelBooking(b.id)}
                            data-testid={`admin-cancel-booking-${b.id}`}
                            className="rounded-full border border-slate-200 px-4 py-1.5 text-xs font-semibold text-slate-500 transition-colors duration-200 hover:border-red-300 hover:text-red-600"
                          >
                            Cancel
                          </button>
                        )}
                        {bookingFilter === "archived" ? (
                          <button
                            onClick={() => archiveBooking(b, false)}
                            data-testid={`restore-booking-${b.id}`}
                            className="flex items-center gap-1.5 rounded-full border border-slate-200 px-4 py-1.5 text-xs font-semibold text-slate-500 transition-colors duration-200 hover:border-emerald-400 hover:text-emerald-600"
                          >
                            <ArchiveRestore className="h-3.5 w-3.5" /> Restore
                          </button>
                        ) : (
                          <button
                            onClick={() => archiveBooking(b, true)}
                            data-testid={`archive-booking-${b.id}`}
                            aria-label={`Archive order ${b.reference}`}
                            className="grid h-8 w-8 place-items-center rounded-full border border-slate-200 text-slate-400 transition-colors duration-200 hover:border-royal hover:text-royal"
                          >
                            <Archive className="h-4 w-4" />
                          </button>
                        )}
                        {(b.status === "cancelled" || bookingFilter === "archived") && (
                          <button
                            onClick={() => deleteBooking(b)}
                            data-testid={`delete-booking-${b.id}`}
                            aria-label={`Delete order ${b.reference}`}
                            className="grid h-8 w-8 place-items-center rounded-full border border-slate-200 text-slate-400 transition-colors duration-200 hover:border-red-300 hover:text-red-600"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          </>
        )}

        {tab === "deliveries" && <DeliveriesTab bookings={bookings} onRefresh={load} />}

        {tab === "quotes" && <QuotesTab listings={listings} />}

        {tab === "activity" && <ActivityTab />}

        {tab === "promos" && <PromosTab />}

        {tab === "team" && <TeamTab />}

        {tab === "customers" && (
          <>
          <div className="mt-8 flex flex-wrap items-center gap-2" data-testid="customer-filters">
            {[["active", "Active"], ["archived", "Archived"]].map(([key, label]) => (
              <button
                key={key}
                onClick={() => setShowArchivedCustomers(key === "archived")}
                data-testid={`customer-filter-${key}`}
                className={`rounded-full px-4 py-2 text-xs font-semibold transition-all duration-200 ${
                  (showArchivedCustomers ? "archived" : "active") === key ? "bg-ink text-white shadow-md" : "border border-slate-200 bg-white text-slate-500 hover:border-royal hover:text-royal"
                }`}
              >
                {label}
              </button>
            ))}
            <input
              value={custSearch}
              onChange={(e) => setCustSearch(e.target.value)}
              placeholder="Filter by name, email or zip…"
              data-testid="customer-search-input"
              className="h-10 min-w-[220px] flex-1 rounded-xl border border-slate-200 bg-white px-4 text-xs font-medium text-ink outline-none transition-colors duration-200 focus:border-royal"
            />
            <a
              href={`${api.defaults.baseURL}/admin/customers/export?archived=${showArchivedCustomers}&q=${encodeURIComponent(custSearch)}`}
              data-testid="export-customers-csv"
              className="rounded-full border border-slate-200 bg-white px-4 py-2 text-xs font-semibold text-slate-500 transition-all duration-200 hover:border-royal hover:text-royal"
            >
              Export CSV
            </a>
          </div>
          <BulkBar count={custSel.size} onClear={() => setCustSel(new Set())} testId="bulk-customers-bar">
            {showArchivedCustomers ? (
              <button onClick={() => bulkCustomers("restore")} data-testid="bulk-customers-restore" className={bulkBtnCls}>
                Restore selected
              </button>
            ) : (
              <button onClick={() => bulkCustomers("archive")} data-testid="bulk-customers-archive" className={bulkBtnCls}>
                Archive selected
              </button>
            )}
            <button onClick={() => bulkCustomers("delete")} data-testid="bulk-customers-delete" className={`${bulkBtnCls} hover:border-red-300 hover:text-red-600`}>
              Delete selected
            </button>
          </BulkBar>
          <div className="mt-4 overflow-x-auto rounded-2xl border border-slate-200 bg-white" data-testid="customers-table">
            <table className="w-full min-w-[820px] text-left text-sm">
              <thead className="border-b border-slate-100 font-mono text-[10px] uppercase tracking-[0.15em] text-slate-400">
                <tr>
                  <th className="w-10 px-4 py-4">
                    <input
                      type="checkbox"
                      checked={sortedCustomers.length > 0 && sortedCustomers.every((c) => custSel.has(c.email))}
                      onChange={() => toggleAllIn(sortedCustomers.map((c) => c.email), setCustSel)}
                      data-testid="customers-select-all"
                      className="h-4 w-4 accent-[#1D4ED8]"
                    />
                  </th>
                  <SortTh label="Customer" k="name" sort={custSort} onSort={toggleCustSort} tidPrefix="customers" />
                  <th className="px-5 py-4">Contact</th>
                  <th className="px-5 py-4">Zip</th>
                  <SortTh label="Orders" k="orders" sort={custSort} onSort={toggleCustSort} tidPrefix="customers" />
                  <SortTh label="Total spent" k="total_spent" sort={custSort} onSort={toggleCustSort} tidPrefix="customers" />
                  <SortTh label="Last order" k="last_booking" sort={custSort} onSort={toggleCustSort} tidPrefix="customers" />
                  <th className="px-5 py-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {sortedCustomers.length === 0 && (
                  <tr><td colSpan={8} className="px-5 py-12 text-center text-slate-400">{showArchivedCustomers ? "No archived customers." : "No customers yet."}</td></tr>
                )}
                {sortedCustomers.map((c) => (
                  <tr key={c.email} data-testid={`customer-row-${c.email}`} className="border-b border-slate-100 transition-colors duration-200 hover:bg-royal-light/40">
                    <td className="px-4 py-4">
                      <input
                        type="checkbox"
                        checked={custSel.has(c.email)}
                        onChange={() => toggleSel(setCustSel)(c.email)}
                        data-testid={`customer-select-${c.email}`}
                        className="h-4 w-4 accent-[#1D4ED8]"
                      />
                    </td>
                    <td className="px-5 py-4">
                      <p className="font-semibold text-ink">{c.name || "—"}</p>
                      {c.has_account ? (
                        <span className="rounded-full bg-emerald-50 px-2 py-0.5 font-mono text-[9px] font-semibold uppercase tracking-[0.12em] text-emerald-600">member</span>
                      ) : (
                        <span className="rounded-full bg-slate-100 px-2 py-0.5 font-mono text-[9px] font-semibold uppercase tracking-[0.12em] text-slate-500">guest</span>
                      )}
                    </td>
                    <td className="px-5 py-4 text-slate-600">
                      <p>{c.email}</p>
                      {c.phone && <p className="text-xs text-slate-400">{c.phone}</p>}
                    </td>
                    <td className="px-5 py-4 text-slate-600">{c.zip || "—"}</td>
                    <td className="px-5 py-4 font-semibold text-ink">{c.orders}</td>
                    <td className="px-5 py-4 font-semibold text-ink">{money(c.total_spent)}</td>
                    <td className="px-5 py-4 text-slate-600">{c.last_booking ? fmtDate(c.last_booking.slice(0, 10)) : "—"}</td>
                    <td className="px-5 py-4">
                      <div className="flex items-center justify-end gap-2">
                        {!showArchivedCustomers && (
                          <button
                            onClick={() => openOrder(c)}
                            data-testid={`customer-new-order-${c.email}`}
                            className="flex items-center gap-1.5 rounded-full border border-slate-200 px-4 py-1.5 text-xs font-semibold text-slate-500 transition-colors duration-200 hover:border-royal hover:text-royal"
                          >
                            <Plus className="h-3.5 w-3.5" /> New order
                          </button>
                        )}
                        {showArchivedCustomers ? (
                          <button
                            onClick={() => archiveCustomer(c, false)}
                            data-testid={`customer-restore-${c.email}`}
                            className="flex items-center gap-1.5 rounded-full border border-slate-200 px-4 py-1.5 text-xs font-semibold text-slate-500 transition-colors duration-200 hover:border-emerald-400 hover:text-emerald-600"
                          >
                            <ArchiveRestore className="h-3.5 w-3.5" /> Restore
                          </button>
                        ) : (
                          <button
                            onClick={() => archiveCustomer(c, true)}
                            data-testid={`customer-archive-${c.email}`}
                            aria-label={`Archive ${c.email}`}
                            className="grid h-8 w-8 place-items-center rounded-full border border-slate-200 text-slate-400 transition-colors duration-200 hover:border-royal hover:text-royal"
                          >
                            <Archive className="h-4 w-4" />
                          </button>
                        )}
                        {c.has_account && (
                          <button
                            onClick={() => deleteCustomer(c)}
                            data-testid={`customer-delete-${c.email}`}
                            aria-label={`Delete ${c.email}`}
                            className="grid h-8 w-8 place-items-center rounded-full border border-slate-200 text-slate-400 transition-colors duration-200 hover:border-red-300 hover:text-red-600"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          </>
        )}

        {tab === "reviews" && (
          <>
          <div className="mt-8 flex flex-wrap items-center gap-2" data-testid="review-filters">
            {[["all", "All"], ["5", "5 stars"], ["4", "4 stars"], ["3", "3 stars"], ["2", "2 stars"], ["1", "1 star"]].map(([key, label]) => (
              <button
                key={key}
                onClick={() => setReviewRating(key)}
                data-testid={`review-filter-${key}`}
                className={`rounded-full px-4 py-2 text-xs font-semibold transition-all duration-200 ${
                  reviewRating === key ? "bg-ink text-white shadow-md" : "border border-slate-200 bg-white text-slate-500 hover:border-royal hover:text-royal"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
          <BulkBar count={reviewSel.size} onClear={() => setReviewSel(new Set())} testId="bulk-reviews-bar">
            <button onClick={bulkReviews} data-testid="bulk-reviews-delete" className={`${bulkBtnCls} hover:border-red-300 hover:text-red-600`}>
              Delete selected
            </button>
          </BulkBar>
          <div className="mt-4 overflow-x-auto rounded-2xl border border-slate-200 bg-white" data-testid="admin-reviews-table">
            <table className="w-full min-w-[720px] text-left text-sm">
              <thead className="border-b border-slate-100 font-mono text-[10px] uppercase tracking-[0.15em] text-slate-400">
                <tr>
                  <th className="w-10 px-4 py-4">
                    <input
                      type="checkbox"
                      checked={visibleReviews.length > 0 && visibleReviews.every((r) => reviewSel.has(r.id))}
                      onChange={() => toggleAllIn(visibleReviews.map((r) => r.id), setReviewSel)}
                      data-testid="reviews-select-all"
                      className="h-4 w-4 accent-[#1D4ED8]"
                    />
                  </th>
                  <th className="px-5 py-4">Reviewer</th>
                  <th className="px-5 py-4">Rating</th>
                  <th className="px-5 py-4">Review</th>
                  <th className="px-5 py-4">Date</th>
                  <th className="px-5 py-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {visibleReviews.length === 0 && (
                  <tr><td colSpan={6} className="px-5 py-12 text-center text-slate-400">No reviews in this view.</td></tr>
                )}
                {visibleReviews.map((r) => (
                  <tr key={r.id} data-testid={`admin-review-row-${r.id}`} className="border-b border-slate-100 transition-colors duration-200 hover:bg-royal-light/40">
                    <td className="px-4 py-4">
                      <input
                        type="checkbox"
                        checked={reviewSel.has(r.id)}
                        onChange={() => toggleSel(setReviewSel)(r.id)}
                        data-testid={`review-select-${r.id}`}
                        className="h-4 w-4 accent-[#1D4ED8]"
                      />
                    </td>
                    <td className="px-5 py-4 font-semibold text-ink">{r.name}</td>
                    <td className="px-5 py-4">
                      <span className="flex items-center gap-0.5">
                        {[1, 2, 3, 4, 5].map((n) => (
                          <Star key={n} className={`h-3.5 w-3.5 ${n <= r.rating ? "fill-gold text-gold" : "text-slate-300"}`} />
                        ))}
                      </span>
                    </td>
                    <td className="max-w-md px-5 py-4">
                      {r.title && <p className="font-semibold text-ink">{r.title}</p>}
                      <p className="line-clamp-2 text-xs text-slate-500">{r.body}</p>
                      {r.image && (
                        <img src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${r.image}`} alt="" className="mt-2 h-12 w-16 rounded-lg border border-slate-200 object-cover" />
                      )}
                    </td>
                    <td className="px-5 py-4 text-slate-600">{r.created_at ? fmtDate(r.created_at.slice(0, 10)) : "—"}</td>
                    <td className="px-5 py-4">
                      <div className="flex justify-end">
                        <button
                          onClick={() => deleteReview(r.id)}
                          data-testid={`review-delete-${r.id}`}
                          className="rounded-full border border-slate-200 px-4 py-1.5 text-xs font-semibold text-slate-500 transition-colors duration-200 hover:border-red-300 hover:text-red-600"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          </>
        )}
      </section>

      <AnimatePresence>
        {modal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-start justify-center overflow-y-auto p-4 py-10"
          >
            <div className="fixed inset-0 bg-ink/60 backdrop-blur-sm" onClick={() => setModal(null)} />
            <motion.form
              onSubmit={submit}
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 24 }}
              transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
              className="relative w-full max-w-2xl rounded-3xl bg-white p-8 shadow-2xl"
              data-testid="listing-form-modal"
            >
              <button
                type="button"
                onClick={() => setModal(null)}
                aria-label="Close form"
                className="absolute right-5 top-5 grid h-9 w-9 place-items-center rounded-full border border-slate-200 text-slate-500 transition-colors duration-200 hover:border-royal hover:text-royal"
              >
                <X className="h-4 w-4" />
              </button>
              <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.25em] text-royal">
                {modal.mode === "add" ? "New package" : "Edit package"}
              </p>
              <h3 className="mt-2 font-display text-2xl font-bold text-ink">
                {modal.mode === "add" ? "Add a package" : "Update package"}
              </h3>
              <div className="mt-6 grid gap-4 sm:grid-cols-2">
                <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Title" required className={`${inputCls} sm:col-span-2`} data-testid="listing-form-title" />
                <input value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} placeholder="Brand" required className={inputCls} />
                <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className={inputCls} data-testid="listing-form-category">
                  {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
                <select value={form.billing} onChange={(e) => setForm({ ...form, billing: e.target.value })} className={inputCls} data-testid="listing-form-billing">
                  <option value="daily">Per-day pricing</option>
                  <option value="weekly">Weekly package pricing</option>
                  <option value="once">One-time price</option>
                  <option value="dual">Dual — daily alone, weekly with package</option>
                  <option value="tiered">Tiered — 1–4 week package pricing</option>
                  <option value="call">Call for pricing</option>
                </select>
                {form.billing === "tiered" ? (
                  <>
                    <input value={form.week1} onChange={(e) => setForm({ ...form, week1: e.target.value })} placeholder="1 week price (USD)" type="number" min="1" required className={inputCls} data-testid="listing-form-week1-price" />
                    <input value={form.week2} onChange={(e) => setForm({ ...form, week2: e.target.value })} placeholder="2 week price (USD)" type="number" min="1" required className={inputCls} data-testid="listing-form-week2-price" />
                    <input value={form.week3} onChange={(e) => setForm({ ...form, week3: e.target.value })} placeholder="3 week price (USD)" type="number" min="1" required className={inputCls} data-testid="listing-form-week3-price" />
                    <input value={form.week4} onChange={(e) => setForm({ ...form, week4: e.target.value })} placeholder="4 week price (USD)" type="number" min="1" required className={inputCls} data-testid="listing-form-week4-price" />
                  </>
                ) : form.billing === "weekly" ? (
                  <>
                    <input value={form.weekly_rate} onChange={(e) => setForm({ ...form, weekly_rate: e.target.value })} placeholder="Weekly rate (USD)" type="number" min="1" required className={inputCls} data-testid="listing-form-weekly-rate" />
                    <input value={form.extra_day_rate} onChange={(e) => setForm({ ...form, extra_day_rate: e.target.value })} placeholder="Extra day rate (USD)" type="number" min="0" required className={inputCls} data-testid="listing-form-extra-day-rate" />
                  </>
                ) : form.billing === "once" ? (
                  <input value={form.flat_rate} onChange={(e) => setForm({ ...form, flat_rate: e.target.value })} placeholder="One-time price (USD)" type="number" min="1" required className={`${inputCls} sm:col-span-2`} data-testid="listing-form-flat-rate" />
                ) : form.billing === "dual" ? (
                  <>
                    <input value={form.daily_rate} onChange={(e) => setForm({ ...form, daily_rate: e.target.value })} placeholder="Daily rate — rented alone (USD)" type="number" min="1" required className={inputCls} data-testid="listing-form-daily-rate" />
                    <input value={form.weekly_rate} onChange={(e) => setForm({ ...form, weekly_rate: e.target.value })} placeholder="Weekly rate — with package (USD)" type="number" min="1" required className={inputCls} data-testid="listing-form-weekly-rate" />
                    <input value={form.extra_day_rate} onChange={(e) => setForm({ ...form, extra_day_rate: e.target.value })} placeholder="Extra day rate (USD)" type="number" min="0" required className={`${inputCls} sm:col-span-2`} data-testid="listing-form-extra-day-rate" />
                  </>
                ) : (
                  <input value={form.daily_rate} onChange={(e) => setForm({ ...form, daily_rate: e.target.value })} placeholder="Daily rate (USD)" type="number" min="1" required className={`${inputCls} sm:col-span-2`} />
                )}
                <input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="Location" className={`${inputCls} sm:col-span-2`} />
                <div className="sm:col-span-2">
                  <div className="flex items-center gap-3">
                    {form.image && (
                      <img src={form.image.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${form.image}` : form.image} alt="Listing preview" className="h-14 w-20 rounded-lg border border-slate-200 object-cover" data-testid="listing-form-image-preview" />
                    )}
                    <label className="flex flex-1 cursor-pointer items-center justify-center gap-2 rounded-xl border border-dashed border-slate-300 bg-paper px-4 py-3.5 text-xs font-semibold text-slate-500 transition-colors duration-200 hover:border-royal hover:text-royal" data-testid="listing-form-image-upload">
                      <ImagePlus className="h-4 w-4" /> Upload photo
                      <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadListingImage(e.target.files?.[0])} data-testid="listing-form-image-input" />
                    </label>
                  </div>
                  <input value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} placeholder="Image URL (or upload above)" required className={`${inputCls} mt-2`} data-testid="listing-form-image-url" />
                </div>
                <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Description" rows={3} className="w-full rounded-xl border border-slate-200 bg-paper px-4 py-3 text-sm font-medium text-ink outline-none transition-colors duration-200 focus:border-royal sm:col-span-2" />
                <textarea value={form.specs} onChange={(e) => setForm({ ...form, specs: e.target.value })} placeholder={"Specs, one per line:\nSensor: 6K Super 35\nMount: PL"} rows={3} className="w-full rounded-xl border border-slate-200 bg-paper px-4 py-3 font-mono text-xs text-ink outline-none transition-colors duration-200 focus:border-royal sm:col-span-2" />
                <label className="flex items-center gap-2.5 text-sm font-medium text-ink">
                  <input type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} className="h-4 w-4 accent-royal" />
                  Featured on homepage
                </label>
                <label className="flex items-center gap-2.5 text-sm font-medium text-ink">
                  <input type="checkbox" checked={form.available} onChange={(e) => setForm({ ...form, available: e.target.checked })} className="h-4 w-4 accent-royal" />
                  Live in catalogue
                </label>
              </div>
              <button
                type="submit"
                disabled={busy}
                data-testid="listing-form-submit-button"
                className="mt-7 w-full rounded-xl bg-royal py-3.5 text-sm font-semibold text-white transition-all duration-200 hover:bg-royal-dark disabled:opacity-50"
              >
                {busy ? "Saving…" : modal.mode === "add" ? "Add package" : "Save changes"}
              </button>
            </motion.form>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {orderOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-start justify-center overflow-y-auto p-4 py-10"
          >
            <div className="fixed inset-0 bg-ink/60 backdrop-blur-sm" onClick={() => setOrderOpen(false)} />
            <motion.form
              onSubmit={submitOrder}
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 24 }}
              transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
              className="relative w-full max-w-xl rounded-3xl bg-white p-8 shadow-2xl"
              data-testid="booking-form-modal"
            >
              <button
                type="button"
                onClick={() => setOrderOpen(false)}
                aria-label="Close order form"
                className="absolute right-5 top-5 grid h-9 w-9 place-items-center rounded-full border border-slate-200 text-slate-500 transition-colors duration-200 hover:border-royal hover:text-royal"
              >
                <X className="h-4 w-4" />
              </button>
              <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.25em] text-royal">
                Manual order — zone check bypassed
              </p>
              <h3 className="mt-2 font-display text-2xl font-bold text-ink">New rental order</h3>
              <p className="mt-1.5 text-xs text-slate-500">
                For customers outside the free delivery zone or phone bookings. Conflicts are still blocked.
              </p>
              <div className="mt-6 grid gap-4 sm:grid-cols-2">
                <select
                  value={orderForm.listing_id}
                  onChange={(e) => setOrderForm({ ...orderForm, listing_id: e.target.value })}
                  className={`${inputCls} sm:col-span-2`}
                  data-testid="booking-form-listing"
                >
                  {listings.filter((l) => l.billing !== "call").map((l) => (
                    <option key={l.id} value={l.id}>{l.title}</option>
                  ))}
                </select>
                <label className="block">
                  <span className="mb-1.5 block font-mono text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">Delivery</span>
                  <input type="date" value={orderForm.start_date} onChange={(e) => setOrderForm({ ...orderForm, start_date: e.target.value })} required className={inputCls} data-testid="booking-form-start" />
                </label>
                <label className="block">
                  <span className="mb-1.5 block font-mono text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">Pickup</span>
                  <input type="date" value={orderForm.end_date} onChange={(e) => setOrderForm({ ...orderForm, end_date: e.target.value })} required className={inputCls} data-testid="booking-form-end" />
                </label>
                <input value={orderForm.name} onChange={(e) => setOrderForm({ ...orderForm, name: e.target.value })} placeholder="Customer name" required className={inputCls} data-testid="booking-form-name" />
                <input value={orderForm.email} onChange={(e) => setOrderForm({ ...orderForm, email: e.target.value })} placeholder="Customer email" type="email" required className={inputCls} data-testid="booking-form-email" />
                <input value={orderForm.phone} onChange={(e) => setOrderForm({ ...orderForm, phone: e.target.value })} placeholder="Phone" required className={inputCls} data-testid="booking-form-phone" />
                <input value={orderForm.zip} onChange={(e) => setOrderForm({ ...orderForm, zip: e.target.value })} placeholder="Delivery zip (any)" inputMode="numeric" maxLength={5} required className={inputCls} data-testid="booking-form-zip" />
                <input value={orderForm.card_holder} onChange={(e) => setOrderForm({ ...orderForm, card_holder: e.target.value })} placeholder="Name on card (card holder)" className={inputCls} data-testid="booking-form-card-holder" />
                <input value={orderForm.license_number} onChange={(e) => setOrderForm({ ...orderForm, license_number: e.target.value })} placeholder="Driver's license number" required className={inputCls} data-testid="booking-form-license" />
                <input value={orderForm.address} onChange={(e) => setOrderForm({ ...orderForm, address: e.target.value })} placeholder="Street address — deliver to / pick up from" required className={`${inputCls} sm:col-span-2`} data-testid="booking-form-address" />
                <select value={orderForm.purpose} onChange={(e) => setOrderForm({ ...orderForm, purpose: e.target.value })} className={inputCls} data-testid="booking-form-purpose">
                  <option value="residential">Residential rental</option>
                  <option value="commercial">Commercial rental</option>
                </select>
                <select value={orderForm.delivery_window} onChange={(e) => setOrderForm({ ...orderForm, delivery_window: e.target.value })} className={inputCls} data-testid="booking-form-delivery-window">
                  <option value="morning">Delivery: 9am–12pm</option>
                  <option value="evening">Delivery: 4pm–7pm</option>
                  <option value="call">Delivery: call to discuss</option>
                </select>
                <select value={orderForm.pickup_window} onChange={(e) => setOrderForm({ ...orderForm, pickup_window: e.target.value })} className={inputCls} data-testid="booking-form-pickup-window">
                  <option value="morning">Pickup: 9am–12pm</option>
                  <option value="evening">Pickup: 4pm–7pm</option>
                  <option value="call">Pickup: call to discuss</option>
                </select>
                <input value={orderForm.alt_name} onChange={(e) => setOrderForm({ ...orderForm, alt_name: e.target.value })} placeholder="Emergency contact name" required className={inputCls} data-testid="booking-form-alt-name" />
                <input value={orderForm.alt_phone} onChange={(e) => setOrderForm({ ...orderForm, alt_phone: e.target.value })} placeholder="Emergency contact phone" required className={inputCls} data-testid="booking-form-alt-phone" />
                <input value={orderForm.alt_email} onChange={(e) => setOrderForm({ ...orderForm, alt_email: e.target.value })} placeholder="Emergency contact email" type="email" required className={inputCls} data-testid="booking-form-alt-email" />
                <textarea value={orderForm.notes} onChange={(e) => setOrderForm({ ...orderForm, notes: e.target.value })} placeholder="Special notes — gate codes, house notes, drop spot (optional)" rows={2} className={`${inputCls} resize-none sm:col-span-2`} data-testid="booking-form-notes" />
                <input value={orderForm.referred_by} onChange={(e) => setOrderForm({ ...orderForm, referred_by: e.target.value })} placeholder="Referred by — name or email (optional)" className={`${inputCls} sm:col-span-2`} data-testid="booking-form-referral" />
                <input type="number" min="0" max="10" value={orderForm.extra_dollies} onChange={(e) => setOrderForm({ ...orderForm, extra_dollies: e.target.value })} placeholder="Extra dollies ($25/wk)" className={inputCls} data-testid="booking-form-dollies" />
                <label className="flex items-center gap-2.5 text-sm font-medium text-ink">
                  <input type="checkbox" checked={orderForm.self_pickup} onChange={(e) => setOrderForm({ ...orderForm, self_pickup: e.target.checked })} className="h-4 w-4 accent-royal" data-testid="booking-form-self-pickup" />
                  Self pick-up & return (−$25)
                </label>
                {listings.find((l) => l.id === orderForm.listing_id)?.billing === "dual" && (
                  <label className="flex items-center gap-2.5 text-sm font-medium text-ink">
                    <input type="checkbox" checked={orderForm.with_package} onChange={(e) => setOrderForm({ ...orderForm, with_package: e.target.checked })} className="h-4 w-4 accent-royal" data-testid="booking-form-with-package" />
                    Rented with a tote package (weekly rate)
                  </label>
                )}
                {listings.find((l) => l.id === orderForm.listing_id)?.billing === "tiered" && (
                  <>
                    <label className="flex items-center gap-2.5 text-sm font-medium text-ink">
                      <input type="checkbox" checked={orderForm.packing_kit} onChange={(e) => setOrderForm({ ...orderForm, packing_kit: e.target.checked })} className="h-4 w-4 accent-royal" data-testid="booking-form-packing-kit" />
                      Add Packing Paper + Cushion Kit ($20)
                    </label>
                    <label className="flex items-center gap-2.5 text-sm font-medium text-ink">
                      <input type="checkbox" checked={orderForm.dolly_bundle} onChange={(e) => setOrderForm({ ...orderForm, dolly_bundle: e.target.checked })} className="h-4 w-4 accent-royal" data-testid="booking-form-dolly-bundle" />
                      Add Tote Dolly + Label Bundle ($25/wk)
                    </label>
                  </>
                )}
              </div>
              <button
                type="submit"
                disabled={busy}
                data-testid="booking-form-submit"
                className="mt-7 w-full rounded-xl bg-royal py-3.5 text-sm font-semibold text-white transition-all duration-200 hover:bg-royal-dark disabled:opacity-50"
              >
                {busy ? "Creating…" : "Create order"}
              </button>
            </motion.form>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {feeModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-center justify-center p-4"
          >
            <div className="fixed inset-0 bg-ink/60 backdrop-blur-sm" onClick={() => setFeeModal(null)} />
            <motion.form
              onSubmit={submitFee}
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 24 }}
              transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
              className="relative w-full max-w-md rounded-3xl bg-white p-8 shadow-2xl"
              data-testid="damage-fee-modal"
            >
              <button
                type="button"
                onClick={() => setFeeModal(null)}
                aria-label="Close fee form"
                className="absolute right-5 top-5 grid h-9 w-9 place-items-center rounded-full border border-slate-200 text-slate-500 transition-colors duration-200 hover:border-royal hover:text-royal"
              >
                <X className="h-4 w-4" />
              </button>
              <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.25em] text-royal">
                Return inspection
              </p>
              <h3 className="mt-2 font-display text-2xl font-bold text-ink">Damage / missing fee</h3>
              <p className="mt-1.5 text-xs text-slate-500">
                {feeModal.reference} · {feeModal.listing_title}
              </p>
              <div className="mt-6 grid grid-cols-2 gap-4">
                <label className="block">
                  <span className="mb-1.5 block font-mono text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">Totes affected</span>
                  <input type="number" min="1" max="200" value={feeForm.tote_count} onChange={(e) => setFeeForm({ ...feeForm, tote_count: e.target.value })} required className={inputCls} data-testid="fee-tote-count" />
                </label>
                <label className="block">
                  <span className="mb-1.5 block font-mono text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">Fee per tote ($15–25)</span>
                  <input type="number" min="15" max="25" step="1" value={feeForm.fee_per_tote} onChange={(e) => setFeeForm({ ...feeForm, fee_per_tote: e.target.value })} required className={inputCls} data-testid="fee-per-tote" />
                </label>
                <input value={feeForm.note} onChange={(e) => setFeeForm({ ...feeForm, note: e.target.value })} placeholder="Note (e.g. 2 cracked lids)" className={`${inputCls} col-span-2`} data-testid="fee-note" />
              </div>
              <div className="mt-4 flex justify-between rounded-xl bg-amber-50 px-4 py-3 text-sm">
                <span className="font-semibold text-amber-800">Fee total</span>
                <span className="font-display font-extrabold text-amber-800" data-testid="fee-total">
                  {money((Number(feeForm.tote_count) || 0) * (Number(feeForm.fee_per_tote) || 0))}
                </span>
              </div>
              <button
                type="submit"
                disabled={busy}
                data-testid="fee-submit-button"
                className="mt-5 w-full rounded-xl bg-royal py-3.5 text-sm font-semibold text-white transition-all duration-200 hover:bg-royal-dark disabled:opacity-50"
              >
                {busy ? "Recording…" : "Record fee"}
              </button>
            </motion.form>
          </motion.div>
        )}
        {detail && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-start justify-center overflow-y-auto p-4 py-10"
          >
            <div className="fixed inset-0 bg-ink/60 backdrop-blur-sm" onClick={() => setDetail(null)} />
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 24 }}
              transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
              className="relative w-full max-w-xl rounded-3xl bg-white p-8 shadow-2xl"
              data-testid="booking-detail-modal"
            >
              <button
                type="button"
                onClick={() => setDetail(null)}
                aria-label="Close details"
                className="absolute right-5 top-5 grid h-9 w-9 place-items-center rounded-full border border-slate-200 text-slate-500 transition-colors duration-200 hover:border-royal hover:text-royal"
              >
                <X className="h-4 w-4" />
              </button>
              <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.25em] text-royal">
                Booking details
              </p>
              <h3 className="mt-2 font-display text-2xl font-bold text-ink">
                {detail.reference} · {detail.listing_title}
              </h3>
              <div className="mt-5 grid gap-4 sm:grid-cols-2">
                <div className="rounded-2xl border border-slate-200 p-4">
                  <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">Customer</p>
                  <p className="mt-2 text-sm font-semibold text-ink" data-testid="detail-customer-name">{detail.customer?.name}</p>
                  <p className="text-xs text-slate-500">{detail.customer?.email} · {detail.customer?.phone}</p>
                  {detail.customer?.card_holder && <p className="text-xs text-slate-500">Card holder: {detail.customer.card_holder}</p>}
                  {detail.customer?.license_number && <p className="text-xs text-slate-500">Driver's license: {detail.customer.license_number}</p>}
                  {detail.customer?.referred_by && <p className="text-xs font-semibold text-amber-700" data-testid="detail-referral">Referred by: {detail.customer.referred_by}</p>}
                  <p className="mt-1 text-xs text-slate-500">
                    {detail.customer?.address ? `${detail.customer.address}, ` : ""}{detail.delivery_zip}{detail.delivery_area ? ` · ${detail.delivery_area}` : ""}
                    {detail.self_pickup ? " · self pick-up" : ""}
                  </p>
                  {detail.customer?.purpose && (
                    <p className="mt-1 text-xs font-semibold capitalize text-royal" data-testid="detail-purpose">{detail.customer.purpose} rental</p>
                  )}
                  {detail.customer?.alt_name && (
                    <p className="mt-1.5 text-xs text-slate-500">
                      Emergency: {detail.customer.alt_name} · {detail.customer.alt_phone} · {detail.customer.alt_email}
                    </p>
                  )}
                  {detail.customer?.notes && (
                    <p className="mt-1.5 rounded-lg bg-paper p-2 text-xs italic text-slate-600" data-testid="detail-notes">"{detail.customer.notes}"</p>
                  )}
                </div>
                <div className="rounded-2xl border border-slate-200 p-4">
                  <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">Rental</p>
                  <p className="mt-2 text-sm font-semibold text-ink">{fmtDate(detail.start_date)} – {fmtDate(detail.end_date)}</p>
                  <p className="text-xs text-slate-500">{detail.days} days · {detail.billing}{detail.with_package ? " · with package" : ""}</p>
                  <p className="mt-1 text-xs text-slate-500">
                    {detail.source === "admin" ? "Manual order" : "Online"} · {detail.status}
                  </p>
                </div>
              </div>
              <div className="mt-4 rounded-2xl border border-slate-200 p-4">
                <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">Pricing</p>
                <div className="mt-2 space-y-1.5 text-sm">
                  <div className="flex justify-between text-slate-600"><span>Rental rate</span><span className="font-semibold text-ink">{money(detail.daily_rate)}</span></div>
                  {detail.dolly_fee > 0 && <div className="flex justify-between text-slate-600"><span>Extra dollies</span><span className="font-semibold text-ink">{money(detail.dolly_fee)}</span></div>}
                  {detail.bundle_fee > 0 && <div className="flex justify-between text-slate-600"><span>Dolly bundle</span><span className="font-semibold text-ink">{money(detail.bundle_fee)}</span></div>}
                  {detail.kit_fee > 0 && <div className="flex justify-between text-slate-600"><span>Packing kit</span><span className="font-semibold text-ink">{money(detail.kit_fee)}</span></div>}
                  {detail.discount > 0 && <div className="flex justify-between text-slate-600"><span>Self-pickup discount</span><span className="font-semibold text-emerald-600">−{money(detail.discount)}</span></div>}
                  <div className="flex justify-between border-t border-slate-100 pt-1.5 text-slate-600"><span>Total</span><span className="font-semibold text-ink">{money(detail.total)}</span></div>
                  <div className="flex justify-between text-slate-600"><span>Deposit paid</span><span className="font-semibold text-ink">{money(detail.amount_reserved || 0)}</span></div>
                  <div className="flex justify-between text-slate-600"><span>Balance due</span><span className="font-semibold text-ink">{money(detail.balance_due || 0)}</span></div>
                </div>
              </div>
              <div className="mt-4 rounded-2xl border border-slate-200 p-4">
                <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">Add-ons & compliance</p>
                <div className="mt-2 flex flex-wrap gap-2 text-xs">
                  {detail.packing_kit && <span className="rounded-full bg-royal-light px-3 py-1 font-semibold text-royal">Packing kit</span>}
                  {detail.dolly_bundle && <span className="rounded-full bg-royal-light px-3 py-1 font-semibold text-royal">Dolly bundle</span>}
                  <span className={`rounded-full px-3 py-1 font-semibold ${detail.agreed_to_terms ? "bg-emerald-50 text-emerald-600" : "bg-slate-100 text-slate-500"}`} data-testid="detail-agreement-status">
                    {detail.agreed_to_terms ? `Agreement accepted${detail.agreement_version ? ` v${detail.agreement_version}` : ""}` : "Pre-agreement (legacy order)"}
                  </span>
                  {(detail.damage_fees_total || 0) > 0 && (
                    <span className={`rounded-full px-3 py-1 font-semibold ${detail.damage_fees_status === "paid" ? "bg-emerald-50 text-emerald-600" : "bg-amber-50 text-amber-700"}`}>
                      {money(detail.damage_fees_total)} damage fees{detail.damage_fees_status ? ` · ${detail.damage_fees_status}` : ""}
                    </span>
                  )}
                </div>
                {detail.agreed_at && (
                  <p className="mt-2 text-[11px] text-slate-400">Agreed {new Date(detail.agreed_at).toLocaleString()}</p>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </main>
  );
}
